import "keen-slider/keen-slider.min.css";
import KeenSlider from "keen-slider";
import "imagesloaded";
import { KeenSliderNavigation } from "../../../../scripts/sections/keen-slider-navigation-plugin";

const MAX_VISIBLE_SLIDES = 5;
const DESKTOP_OVERFLOW_CLASS = "lg:tw-hidden";

type FsSource = { url: string; type: string; thumb?: string };

type FsLightboxProps = {
  sources: string[];
  types: string[];
  thumbs: string[];
  disableThumbs: boolean;
  showThumbsOnMount: boolean;
  /** When true, clicks on empty modal area (not on the slide image) do not close the lightbox. */
  disableBackgroundClose?: boolean;
  onShow?: (instance: FsLightboxInstance) => void;
  onOpen?: (instance: FsLightboxInstance) => void;
  onSlideChange?: (instance: FsLightboxInstance) => void;
};

type FsLightboxInstance = {
  props: FsLightboxProps;
  open: (index?: number) => void;
  stageIndexes?: { current: number };
  elements?: {
    container: HTMLElement | null;
    thumbsWrappers?: (HTMLElement | null | undefined)[];
  };
};

export class ProductGallery {
  private slider: HTMLElement | null;
  private activeIdx: number;
  private wrapper: HTMLElement;
  private container: HTMLElement | null;
  private slides: HTMLElement[] | null;
  private fsSources: FsSource[];

  constructor(wrapper: HTMLElement) {
    this.slider = null;
    this.activeIdx = 0;
    this.wrapper = wrapper;
    this.container =
      wrapper.querySelector<HTMLElement>(".keen-slider") ||
      wrapper.querySelector<HTMLElement>("product-gallery");
    this.fsSources = [];
    this.slides = null;
  }

  /**
    Builds a new instance of the KeenSlider and assigns it to the slider property of the ProductGallery instance.
  */
  _build = () => {
    if (!this.container || !this.slides?.length) {
      return;
    }
    this.slider = new KeenSlider(
      this.container,
      {
        selector: () => this.slides,
        breakpoints: {
          "(min-width: 1024px)": {
            slides: {
              perView: 0,
              spacing: 0,
            },
          },
        },
        slides: {
          perView: 1,
          spacing: 0,
        },
      },
      [
        KeenSliderNavigation({
          arrows: false,
          dots: this.wrapper.querySelector("[data-dots]"),
          wrapper: this.wrapper.querySelector(
            ".keen-slider__navigation-wrapper"
          ),
        }),
      ]
    );
  };

  /**
    Loads videos and reveals them after images are loaded.
  */
  _loadVideos = () => {
    const videos = this.container!.querySelectorAll(".product-gallery__video");

    // reveal videos after images are loaded
    videos.forEach((el: HTMLElement) => {
      el.style.opacity = "1";
    });
  };

  private parseFsSources(): FsSource[] {
    const scriptEl = this.container?.querySelector(
      "[data-product-gallery-fs-sources]"
    );
    if (!scriptEl?.textContent?.trim()) {
      return [];
    }
    try {
      const parsed = JSON.parse(scriptEl.textContent) as FsSource[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Keeps zoom/lightbox URLs in sync after variant-radios fetches fresh section HTML (`?variant=`). */
  private refreshFsSourcesFromDom = (): void => {
    this.fsSources = this.parseFsSources();
  };

  private shouldIgnoreLightboxClick(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el?.closest) {
      return false;
    }
    if (el.closest('a[href]:not([href="#"])')) {
      return true;
    }
    if (el.closest("button")) {
      return true;
    }
    if (el.closest("video")) {
      return true;
    }
    return false;
  }

  private static syncFsLightboxThumbWrapperSelected(
    instance: FsLightboxInstance
  ): void {
    const idx = instance.stageIndexes?.current ?? 0;
    const container = instance.elements?.container;
    const fromProps = instance.elements?.thumbsWrappers?.filter(
      (w): w is HTMLElement => Boolean(w)
    );

    let wrappers: HTMLElement[] = [];
    if (container) {
      const fromDom = [
        ...container.querySelectorAll<HTMLElement>(".fslightbox-thumb-wrapper"),
      ];
      if (fromDom.length > 0) {
        wrappers = fromDom;
      }
    }
    if (!wrappers.length && fromProps?.length) {
      wrappers = fromProps;
    }

    wrappers.forEach((w, i) => {
      w.classList.toggle("selected", i === idx);
    });
  }

  private static scheduleFsLightboxThumbSelectedSync(
    instance: FsLightboxInstance
  ): void {
    ProductGallery.syncFsLightboxThumbWrapperSelected(instance);
    window.requestAnimationFrame(() => {
      ProductGallery.syncFsLightboxThumbWrapperSelected(instance);
      window.setTimeout(
        () => ProductGallery.syncFsLightboxThumbWrapperSelected(instance),
        50
      );
      window.setTimeout(
        () => ProductGallery.syncFsLightboxThumbWrapperSelected(instance),
        200
      );
      window.setTimeout(
        () => ProductGallery.syncFsLightboxThumbWrapperSelected(instance),
        500
      );
    });
  }

  private openFsLightbox(startIndex: number): void {
    const sources = this.fsSources;
    if (!sources.length) {
      return;
    }

    const applyAndOpen = (): boolean => {
      const Fs = (
        window as Window & { FsLightbox?: new () => FsLightboxInstance }
      ).FsLightbox;
      if (!Fs) {
        return false;
      }
      const fs = new Fs();
      fs.props.sources = sources.map((s) => s.url);
      fs.props.disableThumbs = false;
      fs.props.showThumbsOnMount = true;
      fs.props.disableBackgroundClose = true;
      fs.props.thumbs = sources.map((s) => s.thumb ?? "");
      fs.props.onOpen = (inst) => {
        ProductGallery.scheduleFsLightboxThumbSelectedSync(inst);
      };
      fs.props.onShow = (inst) => {
        ProductGallery.scheduleFsLightboxThumbSelectedSync(inst);
      };
      fs.props.onSlideChange = (inst) => {
        ProductGallery.syncFsLightboxThumbWrapperSelected(inst);
      };
      sources.forEach((s, i) => {
        if (s.type === "video") {
          fs.props.types[i] = "video";
        } else if (s.type === "youtube") {
          fs.props.types[i] = "youtube";
        }
      });
      const i = Math.max(0, Math.min(startIndex, sources.length - 1));
      fs.open(i);
      return true;
    };

    if (applyAndOpen()) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 100;
    const tick = () => {
      if (applyAndOpen()) {
        return;
      }
      if (++attempts < maxAttempts) {
        window.requestAnimationFrame(tick);
      }
    };
    window.requestAnimationFrame(tick);

    const onLoad = () => {
      applyAndOpen();
    };
    window.addEventListener("load", onLoad, { once: true });
  }

  private bindLightbox(slideNodes: HTMLElement[]): void {
    if (!this.fsSources.length) {
      return;
    }

    slideNodes.forEach((slide) => {
      slide.addEventListener("click", (e) => {
        if (this.shouldIgnoreLightboxClick(e.target)) {
          return;
        }
        const raw = slide.getAttribute("data-fs-source-index");
        const idx =
          raw !== null && raw !== ""
            ? parseInt(raw, 10)
            : 0;
        this.openFsLightbox(Number.isFinite(idx) ? idx : 0);
      });
    });

    const viewAll = this.wrapper.querySelector<HTMLButtonElement>(
      "[data-product-gallery-view-all]"
    );
    viewAll?.addEventListener("click", () => this.openFsLightbox(0));
  }

  private applySlideOverflow(allSlides: HTMLElement[]): HTMLElement[] {
    const viewAll = this.wrapper.querySelector<HTMLButtonElement>(
      "[data-product-gallery-view-all]"
    );

    allSlides.forEach((slide) => {
      slide.hidden = false;
      slide.removeAttribute("aria-hidden");
      slide.classList.remove(DESKTOP_OVERFLOW_CLASS);
    });

    if (allSlides.length <= MAX_VISIBLE_SLIDES) {
      viewAll?.setAttribute("hidden", "");
      return allSlides;
    }

    allSlides.forEach((slide, index) => {
      if (index >= MAX_VISIBLE_SLIDES) {
        slide.classList.add(DESKTOP_OVERFLOW_CLASS);
      }
    });

    viewAll?.removeAttribute("hidden");

    return allSlides;
  }

  _init = () => {
    if (!this.container) {
      return;
    }

    this.fsSources = this.parseFsSources();

    this.wrapper.removeEventListener(
      "oli:product-gallery-fs-sources-updated",
      this.refreshFsSourcesFromDom
    );
    this.wrapper.addEventListener(
      "oli:product-gallery-fs-sources-updated",
      this.refreshFsSourcesFromDom
    );

    const allSlides = [
      ...this.container.querySelectorAll<HTMLElement>("product-gallery-slide"),
    ];

    this.slides = this.applySlideOverflow(allSlides);

    this.bindLightbox(allSlides);
    this._loadVideos();
    if (this.slides.length) {
      this._build();
    }
  };
}
